import { useMemo } from "react";

import { Button, Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { CoachRating } from "@/components/pages_flexible/coach/CoachRating";
import { ProfileDetails } from "@/components/pages_flexible/coach/ProfileDetails";
import { mappingGroupedWorkHistories } from "@/components/pages_flexible/coach/workHistoryUtils";
import { OnboardButton } from "@/components/pieces/onboarding/CtaButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { CARD_CLASSES, SUBTEXT_COLOR, TEAL } from "@/css/constants";
import { useGetAllCoachProductsQuery } from "@/graphql";
import { ListAgentProfile } from "@/types/coaches";
import { ensureNonNullArray } from "@/utils/array";
import { formatCreditsLowerCase } from "@/utils/string";

interface CoachCardProps {
  profile: ListAgentProfile;
  disabled: boolean;
  loading: boolean;
  active: boolean;
  setActiveProfile: (profile: ListAgentProfile) => void;
  onProfileSelect: (profile: ListAgentProfile) => void;
}

export function CoachCard({
  profile,
  disabled,
  loading,
  active,
  setActiveProfile,
  onProfileSelect,
}: CoachCardProps) {
  const { user, summary } = profile;
  const onboardButton = (
    <OnboardButton
      onClick={() => onProfileSelect(profile)}
      intent="primary"
      large={true}
      fill={true}
      disabled={disabled}
      loading={loading}
    >
      Work with {user?.firstName}
    </OnboardButton>
  );

  return (
    <Box
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      height="100%"
      overflow="hidden"
    >
      <Flex flexDirection="column" height="100%">
        <Flex alignItems="center" className="bbs" p={3}>
          <Avatar user={user} size={100} borderColor={Colors.WHITE} />
          <Box ml={3}>
            <Box fontWeight={600} fontSize={24}>
              {user?.name}
            </Box>
            <Flex alignItems="center" flexWrap="wrap" mt={1}>
              <CoachRating rating={profile.averageRating} />
            </Flex>
          </Box>
        </Flex>
        <Flex
          p={3}
          flex={1}
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <RichContentRenderer content={summary} />
            <MatchingExpertise profile={profile} />
            <CoachCreditRange coachId={profile.user?.id} />
            <WorkHistory profile={profile} />
          </Box>
          <Box mt={24}>
            {onboardButton}
            <Box mt={3}>
              {active ? (
                <Media at="xs">
                  <ProfileDetails
                    profile={profile}
                    containerCss={{ marginTop: 32 }}
                    isAssignedCoach={false}
                  />
                  <Box mt={24}>{onboardButton}</Box>
                </Media>
              ) : (
                <Button
                  onClick={() => {
                    setActiveProfile(profile);
                  }}
                  fill={true}
                  large={true}
                >
                  More about {user?.firstName}
                </Button>
              )}
            </Box>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}

function CoachCreditRange({ coachId }: { coachId?: string }) {
  const { data } = useGetAllCoachProductsQuery({
    variables: {
      coachId: coachId || "",
    },
    skip: !coachId,
  });

  const minCredit = data?.productsByCoachId.minCredit;
  const maxCredit = data?.productsByCoachId.maxCredit;

  if (!minCredit || !maxCredit) return null;

  const creditsString = formatCreditsLowerCase(minCredit);
  const maxCreditsString = formatCreditsLowerCase(maxCredit);

  return (
    <Flex mt={2} alignItems="center">
      <EmbedSvg src={CreditSVG} height={18} width={18} />
      <Box ml={2} alignItems="center">
        Services cost{" "}
        {maxCredit === minCredit
          ? creditsString
          : `between ${creditsString} and ${maxCreditsString}`}
      </Box>
    </Flex>
  );
}

interface ExpertiseRowProps {
  prefix: string;
  items: string[];
  textTransform: string;
}

function ExpertiseRow({ prefix, items, textTransform }: ExpertiseRowProps) {
  function expertiseString(items: string[]) {
    const nodes = [...items]
      .sort((a, b) => a.length - b.length)
      .map((item) => (
        <span
          key={item}
          style={{
            backgroundColor: "rgba(38, 198, 202, 0.07)",
            textTransform: textTransform as "capitalize" | "lowercase",
          }}
        >
          {item}
        </span>
      ));
    if (nodes.length > 2) {
      return (
        <>
          {nodes[0]}, {nodes[1]}, and more
        </>
      );
    } else if (nodes.length === 2) {
      return (
        <>
          {nodes[0]} and {nodes[1]}
        </>
      );
    } else {
      return nodes[0];
    }
  }

  return (
    <Flex mb={2}>
      <Box css={{ marginTop: 2, minWidth: 18 }}>
        <Icon icon="tick" color={TEAL} size={18} />
      </Box>
      <Box ml={2}>
        {prefix} {expertiseString(items)}
      </Box>
    </Flex>
  );
}

interface MatchingExpertiseProps {
  profile: ListAgentProfile;
}

export function MatchingExpertise({ profile }: MatchingExpertiseProps) {
  const matchingProficiencies = ensureNonNullArray(
    profile.matchingProficiencies,
  );
  const matchingIndustries = ensureNonNullArray(profile.matchingIndustries);
  const matchingJobFunctions = ensureNonNullArray(profile.matchingJobFunctions);

  if (
    !matchingProficiencies.length &&
    !matchingIndustries.length &&
    !matchingJobFunctions.length
  ) {
    return null;
  }

  return (
    <Box mt={24} mb={2}>
      {!!matchingJobFunctions.length && (
        <ExpertiseRow
          prefix="Works with roles like"
          items={matchingJobFunctions}
          textTransform="capitalize"
        />
      )}
      {!!matchingIndustries.length && (
        <ExpertiseRow
          prefix="Experience in"
          items={matchingIndustries}
          textTransform="capitalize"
        />
      )}
      {!!matchingProficiencies.length && (
        <ExpertiseRow
          prefix="Can help with"
          items={matchingProficiencies}
          textTransform="lowercase"
        />
      )}
    </Box>
  );
}

function WorkHistory({ profile }: { profile: ListAgentProfile }) {
  const workHistories = profile.user?.workHistories ?? [];

  const groupedWorkHistories = useMemo(
    () => mappingGroupedWorkHistories(workHistories),
    [workHistories],
  );

  if (workHistories.length === 0) return null;

  return (
    <Box mt={24}>
      <Box
        css={{
          color: SUBTEXT_COLOR,
          textTransform: "uppercase",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        Worked at
      </Box>
      <Flex mt={2}>
        {groupedWorkHistories.slice(0, 4).map((whg, i) => {
          return (
            <Tooltip
              content={
                <Box maxWidth="200px" style={{ overflowWrap: "break-word" }}>
                  {whg.work[0].jobTitles} at {whg.name}
                </Box>
              }
              key={i}
            >
              <Logo ml={i > 0 ? "12px" : 0}>
                <img src={whg.logo} />
              </Logo>
            </Tooltip>
          );
        })}
      </Flex>
    </Box>
  );
}

const Logo = styled(Box)`
  img {
    max-height: 40px;
    max-width: 40px;
  }
`;
